const fallbackId = () => globalThis.crypto?.randomUUID?.() || `ledger-${Date.now()}-${Math.random().toString(16).slice(2)}`;

const text = (value, fallback = "", max = 1000) =>
  (typeof value === "string" ? value : fallback).slice(0, max);

const money = (value, { signed = false } = {}) => {
  const parsed = Number(value);
  if (!Number.isFinite(parsed)) return 0;
  const rounded = Math.round(parsed);
  return signed
    ? Math.max(-100_000_000, Math.min(100_000_000, rounded))
    : Math.max(0, Math.min(100_000_000, rounded));
};

const dateOnly = (value) => /^\d{4}-\d{2}-\d{2}$/.test(value || "") ? value : "";

export const blankCareer = () => ({
  startingNuyen: 0,
  jobs: [],
  advancements: [],
  adjustments: [],
});

export const normalizeCareer = (raw, makeId = fallbackId) => {
  const source = raw && typeof raw === "object" && !Array.isArray(raw) ? raw : {};
  return {
    startingNuyen: money(source.startingNuyen),
    jobs: (Array.isArray(source.jobs) ? source.jobs : []).filter(Boolean).map((job) => ({
      id: text(job.id, "", 160) || makeId(),
      name: text(job.name, "Unnamed job", 160),
      completedOn: dateOnly(job.completedOn),
      client: text(job.client, "", 160),
      payout: money(job.payout),
      expenses: money(job.expenses),
      notes: text(job.notes, "", 1000),
    })),
    advancements: (Array.isArray(source.advancements) ? source.advancements : []).filter(Boolean).map((item) => ({
      id: text(item.id, "", 160) || makeId(),
      purchasedOn: dateOnly(item.purchasedOn),
      type: text(item.type, "custom", 80),
      label: text(item.label, "Unnamed advancement", 240),
      cost: money(item.cost),
      details: text(item.details, "", 1000),
    })),
    adjustments: (Array.isArray(source.adjustments) ? source.adjustments : []).filter(Boolean).map((item) => ({
      id: text(item.id, "", 160) || makeId(),
      recordedOn: dateOnly(item.recordedOn),
      label: text(item.label, "Nuyen adjustment", 240),
      amount: money(item.amount, { signed: true }),
    })),
  };
};

export const careerTotals = (career) => {
  const normalized = normalizeCareer(career);
  const gross = normalized.jobs.reduce((sum, job) => sum + job.payout, 0);
  const jobExpenses = normalized.jobs.reduce((sum, job) => sum + job.expenses, 0);
  const advancementSpent = normalized.advancements.reduce((sum, item) => sum + item.cost, 0);
  const adjustments = normalized.adjustments.reduce((sum, item) => sum + item.amount, 0);
  const netEarned = gross - jobExpenses;
  return {
    jobsCompleted: normalized.jobs.length,
    gross,
    jobExpenses,
    netEarned,
    advancementSpent,
    adjustments,
    balance: normalized.startingNuyen + netEarned + adjustments - advancementSpent,
  };
};

export const hasCareerActivity = (career) => {
  const normalized = normalizeCareer(career);
  return normalized.startingNuyen !== 0 || normalized.jobs.length > 0 || normalized.advancements.length > 0 || normalized.adjustments.length > 0;
};

export const nextAttributeCost = (current, metatypeMaximum) => {
  if (current >= metatypeMaximum) return null;
  return current + 1 === metatypeMaximum ? 20_000 : 10_000;
};

export const nextSkillCost = (current) => {
  if (current >= 8) return null;
  return current + 1 <= 5 ? 2_500 : 5_000;
};
